import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { InventoryDetail } from '@cloudageone-schemas/InventoryDetail';
import { LOOKUP_ENUM } from 'app/dictionary/core/lookup.enum';
import { OrderInventoryItem, OrderManageDialogComponent } from 'app/order/shared/order-manage-dialog/order-manage-dialog.component';
import { PermissionService } from 'app/permissions/core/permission.service';
import { GridColumn } from 'app/shared/generic-grid/generic-grid.component';
import { GridDataSource } from 'app/shared/generic-grid/types/GridDataSource';
import { PAGE_NAME } from 'app/shared/generic-grid/types/page-name.constants';
import { RowDblClickEvent } from 'devextreme/ui/data_grid';
import { Item } from 'devextreme/ui/toolbar';
import { firstValueFrom, forkJoin } from 'rxjs';
import { ConfigService } from '../../../core/config/config.service';
import { IMessagesResourceService, ResourcesService } from '../../../core/resources/resources.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { DialogService } from '../../../shared/dialog/dialog.service';
import { SnakeCaseDeep } from '../../../utils/snake-case-deep';
import { InventoryService } from '../../core/inventory.service';
import { InventoryDetailService } from '../../services/inventory-detail.service';
import { InventoryManageDialogComponent } from '../../shared/inventory-manage-dialog/inventory-manage-dialog.component';
import { InventoryFilters } from './constants/filters';

@Component({
	selector: 'app-inventory-grid',
	templateUrl: './inventory-grid.component.html',
	styleUrls: ['./inventory-grid.component.scss'],
	standalone: false
})
export class InventoryGridComponent implements OnInit {
	@Input() pageName!: PAGE_NAME;
	@ViewChild('sidenav') sidenav!: MatSidenav;

	selectedRows: InventoryDetail[] = [];
	selectedRowKeys: number[] = [];
	selectedRow?: InventoryDetail;

	dataSource = new GridDataSource<InventoryDetail>('id', (params) => firstValueFrom(this.inventoryDetailService.findAndCountMany(params)));

	columns: GridColumn<InventoryDetail>[] = [
		{ dataField: 'spCktId', caption: 'SP Service ID', width: 200 },
		{ dataField: 'id', caption: 'System ID', width: 150, alignment: 'left', visible: false },
		{ dataField: 'type.value', caption: 'Network Type', width: 130 },
		{ dataField: 'vendor.name', caption: 'Vendor', width: 130 },
		{ dataField: 'customerInventory.customerCircuitId', caption: 'Customer Service ID', width: 130 },
		{ dataField: 'installDate', caption: 'Install Date', dataType: 'date', alignment: 'left', width: 130 },
		{ dataField: 'discDate', caption: 'Disconnect Date', dataType: 'date', alignment: 'left', width: 130 },
		{
			dataField: 'siteA.building.address.searchAddress',
			caption: 'Site A (Carrier Location)',
			alignment: 'left',
			width: 200,
			cellTemplate: 'siteATemplate',
			calculateCellValue: (rowData) => `${rowData.siteA?.siteId}: ${rowData.siteA?.building?.address?.searchAddress}`
		},
		{
			dataField: 'siteZ.building.address.searchAddress',
			caption: 'Site Z (Customer Location)',
			alignment: 'left',
			width: 200,
			cellTemplate: 'siteZTemplate',
			calculateCellValue: (rowData) => `${rowData.siteZ?.siteId}: ${rowData.siteZ?.building?.address?.searchAddress}`
		},
		{ dataField: 'status.value', caption: 'Status', width: 130 },
		{ dataField: 'internalId', caption: 'Internal ID', width: 200 },
		{ dataField: 'inventoryCktType.value', caption: 'Service Type', width: 150 },
		{ dataField: 'technology.value', caption: 'Svc Tech', width: 150 },
		{ dataField: 'circuitBandwidth.value', caption: 'Service Bandwidth', width: 150 },
		{ dataField: 'bandwidthMax.value', caption: 'Port Bandwidth', width: 150 },
		{ dataField: 'vlanId', caption: 'VLAN ID', width: 150 },
		{ dataField: 'isCustomerProvidedAccess', caption: 'Customer Provided Access', width: 120 },
		{ dataField: 'customer.company', caption: 'Customer', width: 150 },
		{ dataField: 'ban', caption: 'Account', width: 150 },
		{ dataField: 'account.subclient.value', caption: 'Subclient', width: 150 },
		{ dataField: 'currency.currency', caption: 'Currency', width: 120 },
		{ dataField: 'estMrc', caption: 'MRC', alignment: 'right', width: 100 },
		{ dataField: 'estNrc', caption: 'NRC', alignment: 'right', width: 100 }
	];

	filters = InventoryFilters;
	toolbarActions: Item[] = [];

	readonly customValuesConfigKey = 'inventory_detail';
	messages: IMessagesResourceService;

	isEnableDisconnect: boolean = false;
	isOrderEnabled: boolean = false;
	public permissionOrderCreate: boolean = false;
	private adjustmentsKeys = Object.keys(LOOKUP_ENUM.ORDER_TYPE)
		.filter((item) => item !== 'INSTALL')
		.map((key) => ({
			key,
			label: key
				.split('_')
				.map((word) => word.charAt(0) + word.slice(1).toLowerCase())
				.join(' ')
		}));

	constructor(
		private inventoryDetailService: InventoryDetailService,
		private inventoryService: InventoryService,
		private dialogService: DialogService,
		public router: Router,
		private alert: AlertService,
		private permissionService: PermissionService,
		private configService: ConfigService
	) {
		this.messages = ResourcesService.messages('inventory');
	}

	ngOnInit(): void {
		this.buildToolbar();
		this.loadPermissions().subscribe(([inventoryModify, orderCreate]) => {
			this.isEnableDisconnect = inventoryModify && this.isOrderEnabled;
			this.permissionOrderCreate = orderCreate;
			this.buildToolbar();
		});
	}

	async newInventory() {
		const hasCustomFields = await this.configService.hasCustomFields(this.customValuesConfigKey);

		this.dialogService
			.open(InventoryManageDialogComponent, { inventory: {} }, { width: hasCustomFields ? '1200px' : '880px', height: '600px' })
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					this.reloadGrid();
					this.alert.success('', this.messages.get('CREATE_SUCCESS'));
				}
			});
	}

	async openEditDialog() {
		if (this.selectedRows.length !== 1) return;

		const hasCustomFields = await this.configService.hasCustomFields(this.customValuesConfigKey);

		this.dialogService
			.edit(InventoryManageDialogComponent, { inventory: this.inventoryService.findByIdForEdit(this.selectedRows[0].id) }, { width: hasCustomFields ? '1200px' : '880px', height: '600px' })
			.subscribe((result: any) => {
				this.reloadGrid();
				if (result.deleted) {
					this.selectedRows = [];
					this.buildToolbar();
				} else {
					this.alert.success('', this.messages.get('UPDATE_SUCCESS'));
				}
			});
	}

	onRowDblClick(event: RowDblClickEvent<InventoryDetail>) {
		this.router.navigate(['/inventory', event.data.id, 'show', 'summary']);
	}

	onSelectionChanged(data: InventoryDetail[]) {
		this.selectedRows = data;
		this.selectedRowKeys = data.map((row) => row.id);
		this.selectedRow = this.selectedRows.length === 1 ? this.selectedRows[0] : undefined;
		this.buildToolbar();
	}

	/** Every row action is always on the toolbar, disabled until the selection allows it. */
	private buildToolbar() {
		const rows = this.selectedRows;

		this.toolbarActions = [
			{
				widget: 'dxDropDownButton',
				options: {
					icon: 'description',
					hint: 'Create Order',
					visible: this.permissionOrderCreate,
					disabled: !this.canCreateOrder(rows),
					items: this.adjustmentsKeys,
					dropDownOptions: {
						width: 'auto',
						minWidth: 0,
						wrapperAttr: {
							class: 'grid-toolbar-dropdown'
						}
					},
					keyExpr: 'key',
					displayExpr: 'label',
					onItemClick: (args: { itemData?: { key?: string } }) => this.createOrder(args.itemData?.key)
				},
				location: 'after'
			},
			{
				widget: 'dxButton',
				options: { icon: 'edit', hint: 'Edit', disabled: rows.length !== 1, onClick: () => this.openEditDialog() },
				location: 'after'
			},
			{
				widget: 'dxButton',
				options: { icon: 'info', hint: 'Info', disabled: rows.length !== 1, onClick: () => this.sidenav.toggle() },
				location: 'after'
			}
		];
	}

	// Orders can be created for one or more Active rows that share a vendor and currency.
	private canCreateOrder(rows: InventoryDetail[]): boolean {
		if (!rows?.length) return false;

		const vendorId = rows[0].vendor?.id;
		const currencyId = rows[0].currency?.id;
		if (vendorId == null || currencyId == null) return false;

		return rows.every((row) => row.status?.value === 'Active' && row.vendor?.id === vendorId && row.currency?.id === currencyId);
	}

	reloadGrid() {
		this.dataSource.source.reload();
	}

	createOrder(type?: string) {
		const inventories = this.buildOrderInventories(this.selectedRows ?? []);

		this.dialogService
			.open(
				OrderManageDialogComponent,
				{
					order: {},
					type: type,
					inventories
				},
				{ width: '1200px' }
			)
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					this.reloadGrid();
				}
			});
	}

	closeSidenav() {
		this.sidenav.close();
	}

	private buildOrderInventories(rows: InventoryDetail[]): OrderInventoryItem[] {
		return SnakeCaseDeep(rows).map((row: any) => ({
			...row,
			vendor_id: row.vendor?.id,
			currency_id: row.currency?.id,
			status_id: row.status?.id
		}));
	}

	private loadPermissions() {
		const inventoryModify$ = this.permissionService.isAllowed('inventory', 'MODIFY');
		const orderCreate$ = this.permissionService.isAllowed('orders', 'CREATE');
		return forkJoin(inventoryModify$, orderCreate$);
	}
}
